import axios, { AxiosError } from 'axios';
import { Api } from '../axios-config';
import { comOffline, somenteOnline } from '../../../offline/servico';
import { conexao } from '../../../offline/conexao';
import {
  abrirSessaoLocal, guardarSessaoOnline, sessaoAtivaLocal, sessaoOfflinePendente,
} from '../../../offline/sessaoOffline';

export type TStatusSessaoCaixa = 'aberta' | 'fechada' | 'cancelada';

export interface ISessaoCaixaAtiva {
  idsessao: string;
  numero: string;
  iduser: string;
  idconta: string;
  nomeConta: string;
  dataabertura: string;
  status: TStatusSessaoCaixa;
  valorabertura?: number;
  nomeUsuario?: string;
}

export interface IFormaPagamentoAbertura {
  valor: number;
  categoria: string;
}

interface ErrorResponse {
  errors?: { default: string };
}

const extractError = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    const message = axiosError.response?.data?.errors?.default;
    if (message) return new Error(message);
    if (axiosError.request) return new Error('Nenhuma resposta do servidor');
    return new Error(axiosError.message);
  }
  return new Error('Erro inesperado');
};

// Sessão de caixa do usuário logado, achável de qualquer computador — nunca
// lida do localStorage (diferente de idContaSelecionada, ver AuthContext),
// sempre desta chamada. Backend garante no máximo uma sessão 'aberta' por
// usuário e por conta (índices únicos parciais em sessaocaixa).
const getAtivaOnline = async (): Promise<ISessaoCaixaAtiva | null | Error> => {
  try {
    const { data } = await Api.get('/sessaocaixa/ativa');
    return data;
  } catch (error) {
    return extractError(error);
  }
};

// Se o usuário já tiver uma sessão aberta (em qualquer caixa), o backend
// devolve essa mesma sessão em vez de criar outra — nunca duas sessões pro
// mesmo usuário. Se a conta já tiver sessão aberta com OUTRO usuário,
// devolve erro.
const abrirOnline = async (
  idconta: string,
  formasPagamento: IFormaPagamentoAbertura[],
): Promise<ISessaoCaixaAtiva | Error> => {
  try {
    const { data } = await Api.post('/sessaocaixa/abrir', { idconta, formasPagamento });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

export interface ISaldoPreviewCaixa {
  saldoanterior: number;
  saldototal: number;
}

// Saldo real do caixa (todo o histórico, sem escopo de turno/sessão) ANTES
// de abrir — pro operador conferir o que já tem fisicamente na gaveta antes
// de informar o valor de abertura. Não depende de sessão ativa (por isso vai
// por query, não pela sessão do usuário logado, que ainda não existe nesse
// momento).
const getSaldoPreviewOnline = async (idconta: string): Promise<ISaldoPreviewCaixa | Error> => {
  try {
    const { data } = await Api.get('/sessaocaixa/saldo', { params: { idconta } });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

export interface IFechamentoConferencia {
  idconta: string;
  valorTransferido: number;
  // Positivo = faltou dinheiro (quebra, debitado do caixa); negativo =
  // sobrou (creditado no caixa); zero = bateu certinho.
  diferenca: number;
}

// Fecha a sessão (e o turno junto — mesmo lançamento, ver
// fecharSessaoComConferencia no backend) só com o valor de Dinheiro
// conferido fisicamente no caixa. Sem escolher conta de destino (sempre o
// Cofre) nem dividir por forma de pagamento (PIX/Cartão já roteiam pra
// recebível na hora da venda, não sobra nada pra "fechar" neles). A
// diferença entre o saldo real do sistema e o valor conferido vira um
// lançamento de quebra/sobra de caixa, devolvido em `diferenca`.
const fecharConferenciaOnline = async (valorConferido: number): Promise<IFechamentoConferencia | Error> => {
  try {
    const { data } = await Api.post('/sessaocaixa/fecharconferencia', { valorConferido });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

export interface IVendaDaSessao {
  idmov: string;
  numero: string;
  emissao: string;
  total: number;
  nomeParceiro: string;
  descricaoOperacao: string;
}

// Todas as vendas do PDV feitas na sessão ativa do usuário logado — botão
// "Relatório" no rodapé.
const getRelatorioVendasOnline = async (): Promise<IVendaDaSessao[] | Error> => {
  try {
    const { data } = await Api.get('/sessaocaixa/relatoriovendas');
    return data;
  } catch (error) {
    return extractError(error);
  }
};

export interface IPagamentoVendaResumo {
  categoria: string;
  valor: number;
}

export type TStatusVendaPdv = 'cancelada' | 'devolvida';

export interface IVendaResumoPDV {
  idmov: string;
  numero: string;
  emissao: string;
  nomeParceiro: string;
  vprod: number;
  desconto: number;
  acrescimo: number;
  total: number;
  quantidadeItens: number;
  pagamentos: IPagamentoVendaResumo[];
  // undefined = venda ativa. Venda cancelada/devolvida continua na lista,
  // só marcada (ver EstornarVendaPdv.ts no backend) — nunca some.
  status?: TStatusVendaPdv;
  statusem?: string;
  nomeAutorizador?: string;
  // Editada por fora do PDV (tela de vendas do ERP) depois de criada — o PDV
  // em si nunca edita uma venda já finalizada, só cria.
  modificada: boolean;
}

export interface IFormaPagamentoResumo {
  categoria: string;
  quantidade: number;
  total: number;
}

export interface IResumoVendasSessao {
  quantidadeVendas: number;
  quantidadeItens: number;
  produtosDiferentes: number;
  valorBruto: number;
  descontos: number;
  acrescimos: number;
  total: number;
  ticketMedio: number;
  formasPagamento: IFormaPagamentoResumo[];
  vendas: IVendaResumoPDV[];
}

// Resumo completo das vendas do PDV feitas na sessão ativa do usuário logado
// (totais, formas de pagamento e a relação de vendas) — dialog "Resumo de
// Vendas do PDV" no rodapé (ver DialogResumoVendasPDV.tsx).
const getResumoVendasOnline = async (): Promise<IResumoVendasSessao | Error> => {
  try {
    const { data } = await Api.get('/sessaocaixa/resumovendas');
    return data;
  } catch (error) {
    return extractError(error);
  }
};

export interface IItemDevolvidoPdv {
  iditens: string;
  idprodutos: string;
  descricao: string;
  marca: string | null;
  referencia: string | null;
  un: string;
  ean: string | null;
  qtds: number;
  preco: number;
}

export interface IResultadoEstornoVendaPdv {
  numero: string;
  valorEstornado: number;
  itens?: IItemDevolvidoPdv[];
}

export interface IItemVendaPdv extends IItemDevolvidoPdv {
  desconto: number;
  voutros: number;
  subtotal: number;
  total: number;
}

// Itens de uma venda (com dados do produto) — botão "ver itens" da Relação
// de Vendas do Resumo. Só leitura, sem autorização nenhuma.
const getItensVendaPdvOnline = async (idmov: string): Promise<IItemVendaPdv[] | Error> => {
  try {
    const { data } = await Api.get(`/vendaspdv/${idmov}/itens`);
    return data.itens;
  } catch (error) {
    return extractError(error);
  }
};

export interface IItemVendaParaEditar {
  iditens: string;
  idprodutos: string;
  un: string;
  qtde: number;
  qtds: number;
  preco: number;
  desconto: number;
  voutros: number;
  subtotal: number;
  total: number;
}

export interface IFormaPagamentoEdicao {
  idoperacao: string;
  valor: number;
  categoria?: string;
  idcontadestino?: string;
}

export interface IDadosEdicaoVendaPdv {
  idoperacao: string;
  vprod: number;
  desconto: number;
  acrescimo: number;
  total: number;
  itens: IItemVendaParaEditar[];
  formasPagamento: IFormaPagamentoEdicao[];
}

// "Editar venda" (puxar a venda pra adicionar item/trocar forma de
// pagamento) — GRAVA NA MESMA VENDA (update), não cria outra. Mesmo padrão
// de autorização por login+senha de cancelar/devolver; nunca offline (mexe
// no caixa na hora).
const editarVendaPdvOnline = async (
  idmov: string, login: string, senha: string, dados: IDadosEdicaoVendaPdv,
): Promise<{ idmov: string } | Error> => {
  try {
    const { data } = await Api.post(`/vendaspdv/${idmov}/editar`, {
      login,
      senha,
      ...dados,
      itens: dados.itens.map((item) => ({ ...item, tipo: 1 })),
    });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

// Cancelamento/devolução do PDV (botões na Relação de Vendas do Resumo, ver
// DialogResumoVendasPDV.tsx) — pede usuário+senha de quem autoriza (pode ser
// outra pessoa, não precisa ser quem está logado) dentro do próprio corpo da
// requisição; o backend confere credenciais E permissão antes de mexer em
// qualquer coisa (ver AutorizarUsuarioPdv.ts/EstornarVendaPdv.ts). Nunca
// offline: é uma operação financeira (estorna caixa na hora), não dá pra
// enfileirar como as vendas normais.
const cancelarVendaPdvOnline = async (idmov: string, login: string, senha: string): Promise<IResultadoEstornoVendaPdv | Error> => {
  try {
    const { data } = await Api.post(`/vendaspdv/${idmov}/cancelar`, { login, senha });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

const devolverVendaPdvOnline = async (idmov: string, login: string, senha: string): Promise<IResultadoEstornoVendaPdv | Error> => {
  try {
    const { data } = await Api.post(`/vendaspdv/${idmov}/devolucao`, { login, senha });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

// ---------- Offline (ver shared/offline/sessaoOffline.ts) ----------
//
// Abertura feita offline e ainda não enviada tem prioridade sobre o
// servidor, mesmo já com internet: até a sincronização enviar a abertura
// (com o valor que o operador informou), o servidor ainda diz "sem sessão"
// — e o PDV abriria sozinho outra sessão com R$ 0 (ver Pdv.tsx).
const getAtiva = async (): Promise<ISessaoCaixaAtiva | null | Error> => {
  const pendente = await sessaoOfflinePendente();
  if (pendente) return pendente;
  return comOffline(getAtivaOnline, sessaoAtivaLocal, guardarSessaoOnline);
};

// Sem internet, abre o caixa localmente e enfileira a abertura pro servidor.
const abrir = async (
  idconta: string,
  formasPagamento: IFormaPagamentoAbertura[],
): Promise<ISessaoCaixaAtiva | Error> => {
  const pendente = await sessaoOfflinePendente();
  if (pendente) return pendente;
  if (conexao.estaOffline()) return abrirSessaoLocal(idconta, formasPagamento);
  const sessao = await abrirOnline(idconta, formasPagamento);
  if (sessao instanceof Error) {
    return conexao.estaOffline() ? abrirSessaoLocal(idconta, formasPagamento) : sessao;
  }
  await guardarSessaoOnline(sessao);
  return sessao;
};

const getSaldoPreview = (idconta: string) => somenteOnline(() => getSaldoPreviewOnline(idconta));

const fecharConferencia = async (valorConferido: number): Promise<IFechamentoConferencia | Error> => {
  const resultado = await somenteOnline(() => fecharConferenciaOnline(valorConferido));
  if (!(resultado instanceof Error)) await guardarSessaoOnline(null);
  return resultado;
};

const getRelatorioVendas = () => somenteOnline(getRelatorioVendasOnline);
const getResumoVendas = () => somenteOnline(getResumoVendasOnline);
const cancelarVendaPdv = (idmov: string, login: string, senha: string) =>
  somenteOnline(() => cancelarVendaPdvOnline(idmov, login, senha));
const devolverVendaPdv = (idmov: string, login: string, senha: string) =>
  somenteOnline(() => devolverVendaPdvOnline(idmov, login, senha));
const getItensVendaPdv = (idmov: string) => somenteOnline(() => getItensVendaPdvOnline(idmov));
const editarVendaPdv = (idmov: string, login: string, senha: string, dados: IDadosEdicaoVendaPdv) =>
  somenteOnline(() => editarVendaPdvOnline(idmov, login, senha, dados));

export const SessaoCaixaService = {
  getAtiva,
  abrir,
  getSaldoPreview,
  fecharConferencia,
  getRelatorioVendas,
  getResumoVendas,
  cancelarVendaPdv,
  devolverVendaPdv,
  getItensVendaPdv,
  editarVendaPdv,
};
